import React, { useState } from 'react'
import Modal from '../../shared/modal'
import PlusMinusWrapper from '../../shared/plusMinusWrapper'
import Icon from '../../shared/icon'
import { ReplenishmentOrderAction, HUMAN_SLOTS } from '../types'
import { t } from 'src/i18n'

type BowlMode = 'view' | 'edit' | 'complete'

const scope = 'replenishment.bowls'

const ReplenishmentCompletedModalContent = ({ closeModal, amount, slotNumber, toggleSidebar }) => {

  const openBasket = () => {
    toggleSidebar(true)
    closeModal()
  }

  return (
    <div className='bowl-replenishment-complete'>
      <div className='title-icon'>
        <Icon name='replace'/>
      </div>
      <div className='heavy-text'>{t('bowls_header', { scope, slotNumber: HUMAN_SLOTS[slotNumber - 1] })}</div>
      <div className='plates-icon-container'>
        <Icon name='plate' color='white'/>
      </div>
      <p>
      {t('bowls_message', { scope, amount: amount })}
      </p>
      <div className='buttons'>
        <button className='btn btn-secondary' onClick={openBasket}>{t('replenishment.replenishment_modal.go_to_basket')}</button>
        <button className='btn btn-primary' onClick={closeModal}>{t('replenishment.replenishment_modal.continue')}</button>
      </div>
    </div>
  )
}

export const BowlReplenishmentModal = ({ containerSlot, container, closeModal, draftReplenishmentOrder, updateCart, toggleSidebar }) => {
  const maxAmount = container?.max_amount || 0
  const amountLeft = container?.remaining_amount ?? 0
  const freeCapacity = container?.replenishment_free_capacity ?? 0
  // Why the cap can sit well below the space visibly left on the stand.
  const alreadyOnOrder = container?.replenishment_on_order ?? 0

  const cartLine = draftReplenishmentOrder.plateReplenishments
    .find(replenishment => replenishment.slotNumber == containerSlot)

  const [mode, setMode] = useState<BowlMode>('view')
  // Seeding free capacity over an existing line turns a glance-and-save into
  // a silent rewrite.
  const [replenishmentAmount, setReplenishmentAmount] = useState(cartLine?.amount ?? freeCapacity)
  const [saveError, setSaveError] = useState<string | null>(null)

  const changeReplenishmentAmount = (passedValue: string | number) => {
    const value = passedValue ? parseInt(`${passedValue}`) : 0
    if (isNaN(value)) return

    setReplenishmentAmount(Math.min(Math.max(0, value), freeCapacity))
  }

  // Another order may have claimed the stand's space since this screen
  // loaded, so the confirmation waits for the server.
  const saveReplenishment = () => {
    setSaveError(null)

    updatePlateReplenishments({ slotNumber: containerSlot, amount: replenishmentAmount })
      .then((error: string | null) => error ? setSaveError(error) : setMode('complete'))
  }

  const updatePlateReplenishments = (newPlateReplenishment) => {
    if(cartLine) {

      const updatedPlateReplenishments = draftReplenishmentOrder.plateReplenishments
        .map(replenishment => (replenishment.slotNumber == newPlateReplenishment.slotNumber)
        ? {...newPlateReplenishment, id: replenishment.id} : replenishment)

      return updateCart({...draftReplenishmentOrder, plateReplenishments: updatedPlateReplenishments}, ReplenishmentOrderAction.ADD_ITEM)
    } else {
      return updateCart({...draftReplenishmentOrder, plateReplenishments: [...draftReplenishmentOrder.plateReplenishments, newPlateReplenishment]}, ReplenishmentOrderAction.ADD_ITEM)
    }
  }

  const headerContent = () => {
    if(cartLine)  {
      return (
        <>
          <h3 className='header'>{t('bowl_stand', {scope, slotNumber: HUMAN_SLOTS[containerSlot - 1]})}</h3>
          <span className='replenishment-header-text'>to refill</span>
        </>
      )
    }

    return (<h3 className='header'>{t('bowl_stand', {scope, slotNumber: HUMAN_SLOTS[containerSlot - 1]})}</h3>)
  }

  const amountText = () => `${amountLeft} / ${maxAmount}`

  const amount = () => {
    if(cartLine) {
      return (
        <>
          <span>{amountLeft} + </span>
          <span style={{color: '#CA56F3'}}>{cartLine.amount}</span>
          <span> / {maxAmount}</span>
        </>
      )
    } else {
      return (
        <span>{amountText()}</span>
      )
    }
  }

  return (
    <>
      <Modal onClose={closeModal}
      headerContent={ mode === 'complete' ? null : headerContent()}>
      <div className='container-modal show'>
      {mode != 'complete' &&
        <>
          <div className='product-info'>
            <div className="plates-icon-container">
              <Icon name='plate' color='white'/>
            </div>
            <div className="product-content">
              {amount()}
            </div>
          </div>
          {alreadyOnOrder > 0 &&
            <div className='plate-stand-notice'>{t('already_on_order', { scope, amount: alreadyOnOrder })}</div>
          }
          {mode === 'edit' &&
            <>
              <div className='attribute-wrapper'>
                <span className='input-label' style={{ marginRight: '10px' }}>Amount</span>
                <PlusMinusWrapper
                  step={10}
                  value={replenishmentAmount}
                  onChange={(_, value) => changeReplenishmentAmount(value)}
                >
                  <div className='subwrapper'>
                    <input
                      type='text'
                      value={replenishmentAmount}
                      maxLength={3}
                      onChange={({ target: { value } }) => changeReplenishmentAmount(value)}
                    />
                  </div>
                </PlusMinusWrapper>
              </div>
              <div className='plate-stand-new-total'>{t('fits_in_stand', { scope, free: freeCapacity })}</div>
            </>
          }
          {saveError && <div className='plate-stand-error'>{saveError}</div>}
          <div className='buttons'>
            <button className='btn btn-secondary' onClick={closeModal}>{t('cancel')}</button>
            {mode === 'view' && <button className='btn btn-primary btn-refill' disabled={freeCapacity <= 0} onClick={() => setMode('edit')}>{t('refill_stand', { scope })}</button>}
            {mode === 'edit' && <button className='btn btn-primary' disabled={replenishmentAmount <= 0} onClick={saveReplenishment}>{t('save')}</button>}
          </div>
        </>
      }
      {mode === 'complete' &&
        <ReplenishmentCompletedModalContent
          closeModal={closeModal}
          toggleSidebar={toggleSidebar}
          amount={replenishmentAmount}
          slotNumber={containerSlot}
        />
      }
      </div>
    </Modal>
    </>
  )
}
